import React, { useState, useEffect, useRef } from 'react';
import { FiDelete, FiX } from 'react-icons/fi';
import './PlayerModal.css';
import './components/HandicapKeypadModal.css';
import { readPlayerDraft, writePlayerDraft, clearPlayerDraft } from './utils/playerDraft';

/**
 * Add or edit one player.
 *
 * `draftKey` opts this modal into keeping a half-finished NEW player across the
 * app being backgrounded or the page being rebuilt — see `utils/playerDraft`.
 * Editing an existing player is never drafted: that name is already stored, and
 * a resurrected edit would be a change nobody asked for.
 */
const PlayerModal = ({ isOpen, onClose, onSave, player, draftKey }) => {
  const [name, setName] = useState('');
  const [handicap, setHandicap] = useState('');
  const [view, setView] = useState('form'); // 'form' or 'keypad'
  const [keypadValue, setKeypadValue] = useState('');
  const [isInitial, setIsInitial] = useState(true);
  const nameRef = useRef(null);

  const drafting = !!draftKey && !player;

  useEffect(() => {
    if (!isOpen) return;
    const draft = drafting ? readPlayerDraft(draftKey) : null;
    setName(draft?.name || player?.name || '');
    setHandicap(draft?.handicap || (player?.handicap != null ? String(player.handicap) : ''));
    setView('form');
  }, [player, isOpen, drafting, draftKey]);

  // The name is what the modal is FOR, and every player added starts by typing
  // it. Focusing it also brings the keyboard up on the tap that opened the
  // modal, rather than costing a second one.
  useEffect(() => {
    if (!isOpen || view !== 'form') return undefined;
    const t = setTimeout(() => nameRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, view]);

  // Written on every keystroke, because the interruption this exists for is
  // not a button press — it is the app going away.
  useEffect(() => {
    if (!isOpen || !drafting) return;
    writePlayerDraft(draftKey, { name, handicap });
  }, [isOpen, drafting, draftKey, name, handicap]);

  if (!isOpen) return null;

  const finish = (fn) => {
    if (drafting) clearPlayerDraft(draftKey);
    fn();
  };

  const handleSave = () => finish(() => onSave({
    ...player,
    name,
    handicap: handicap === '' ? null : parseFloat(handicap)
  }));

  // Keypad logic
  const openKeypad = () => {
    setKeypadValue(handicap);
    setIsInitial(true);
    setView('keypad');
  };

  const handleKey = (key) => {
    setKeypadValue(prev => {
      if (isInitial && key !== '-' && key !== '.') {
        setIsInitial(false);
        return key;
      }
      setIsInitial(false);
      if (key === '-') {
        return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
      }
      if (key === '.') {
        if (prev.includes('.')) return prev;
        if (prev === '' || prev === '-') return prev + '0.';
        return prev + '.';
      }
      const dotIndex = prev.indexOf('.');
      if (dotIndex !== -1 && prev.length - dotIndex > 1) return prev;
      const abs = prev.startsWith('-') ? prev.slice(1) : prev;
      if (abs === '0' && key !== '.') return prev.startsWith('-') ? '-' + key : key;
      return prev + key;
    });
  };

  const handleBackspace = () => {
    setIsInitial(false);
    setKeypadValue(prev => prev.slice(0, -1));
  };

  const handleKeypadSave = () => {
    const parsed = parseFloat(keypadValue);
    if (keypadValue === '' || isNaN(parsed)) return;
    setHandicap(String(parsed));
    setView('form');
  };

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '-', '0', '.'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={view === 'keypad' ? 'hkm-modal' : 'modal-content'}
        onClick={(e) => e.stopPropagation()}
      >
        {view === 'form' ? (
          <>
            <h2>{player ? 'Edit Player' : 'Add Player'}</h2>
            <div className="form-group">
              <label htmlFor="name">Player name</label>
              <input
                ref={nameRef}
                className="pm-input"
                type="text"
                id="name"
                value={name}
                placeholder="Player Name"
                autoComplete="off"
                autoCapitalize="words"
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Handicap</label>
              <div className="handicap-keypad-trigger" onClick={openKeypad}>
                {handicap !== '' ? handicap : <span className="handicap-placeholder">Tap to set</span>}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleSave} className="btn-blue">Save</button>
              <button onClick={() => finish(onClose)} className="btn-grey">Cancel</button>
            </div>
          </>
        ) : (
          <>
            <button className="hkm-close" onClick={() => setView('form')}><FiX size={16} /></button>
            <div className="hkm-header">
              <span className="hkm-label">Handicap Index</span>
              <div className="hkm-divider" />
              <div className="hkm-display">
                {keypadValue || '0'}
              </div>
            </div>
            <div className="hkm-keypad">
              {keys.map(k => (
                <button
                  key={k}
                  className={`hkm-key ${k === '-' ? 'hkm-key--minus' : ''}`}
                  onClick={() => handleKey(k)}
                >
                  {k === '-' ? '+/-' : k}
                </button>
              ))}
            </div>
            <div className="hkm-bottom-row">
              <button className="hkm-key hkm-key--backspace" onClick={handleBackspace}>
                <FiDelete size={20} />
              </button>
              <button
                className="hkm-save"
                onClick={handleKeypadSave}
                disabled={keypadValue === '' || isNaN(parseFloat(keypadValue))}
              >
                Save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerModal;
